import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { getFightCategoryLabelById } from 'web/data/gui-data'
import { Fight } from 'web/data/models'
import { DisplayFightActions } from 'web/redux/slices/display-fight-slice'
import { FightActions } from 'web/redux/slices/fight-slice'
import { SetupActions } from 'web/redux/slices/setup-slice'
import { useAppDispatch } from 'web/redux/store'
import { ModalContainerProps } from './_ModalContainer'

export type PruneOutlinesModalProps = {
    allFights: Fight[]
    legacyFights: Fight[]
}

export const PruneOutlinesModal: React.FC<PruneOutlinesModalProps & ModalContainerProps> = ({
    allFights, legacyFights, closeSelf
}) => {

    const [state, setState] = useState<'idle' | 'pending'>('idle')

    const fightNames = legacyFights
        .map(fight => `${getFightCategoryLabelById(fight.categoryId)} - ${fight.name}`)
        .sort()

    const disp = useAppDispatch()

    const wrappedCloseSelf = () => {
        if (state === "idle") {
            closeSelf()
        }
    }

    const doPrune = () => {
        setState('pending')

        // Delay this to let the spinner get going
        setTimeout(() => {
            const runLast = () => {
                legacyFights.forEach(fight => {
                    disp(SetupActions.deleteSetupsForFight({ fightId: fight.id }))
                    disp(FightActions.deleteFight({ fightId: fight.id }))
                })

                wrappedCloseSelf()
            }

            // Everything is about to be deleted, so we'll add a dummy fight
            if (legacyFights.length === allFights.length) {

                disp(DisplayFightActions.clearSelectedSetup())

                disp(FightActions.addDummyFight())
                    .then(unwrapResult)
                    .then(({ fightId }) => {
                        disp(DisplayFightActions.selectFight({ fightId }))
                        runLast()
                    })

            } else {
                runLast()
            }
        }, 200)
    }

    return (
        <Modal
            isOpen
            fade={false}
            toggle={wrappedCloseSelf}
            backdrop={"static"}
        >

            <ModalHeader>
                {`Prune historical outlines`}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">
                {`The following outlines belong to encounters from an earlier patch. Do you want to delete them and all of their associated setups?`}

                <ul className="small m-0 mt-3">
                    {fightNames.map(name => (
                        <li key={name}>
                            {name}
                        </li>
                    ))}
                </ul>
            </ModalBody>

            <ModalFooter className="d-flex justify-content-end space-mx-2" >
                <Button
                    size="sm"
                    onClick={doPrune}
                    color={'danger'}
                >

                    {state === "idle" && (
                        <>
                            {`Yes, delete these outlines and their setups`}
                        </>
                    )}

                    {state === "pending" && (
                        <FontAwesomeIcon
                            className={"mx-5"}
                            pulse
                            fixedWidth
                            icon={faSpinner}
                        />
                    )}

                </Button>

                <Button
                    outline
                    size="sm"
                    onClick={wrappedCloseSelf}
                    title={"Cancel (esc)"}
                >
                    {`Cancel`}
                </Button>
            </ModalFooter>

        </Modal>
    )
}
